import './styles.css';
import { api, ApiError, loadTheme } from './api';
import { Board } from './board';
import { Notepad } from './notepad';
import type { Symbol as NoteSymbol } from './notepad';
import { cardRef, cardType } from './types';
import type { Bootstrap, GameSnapshot, GameSummary, Phase, Theme } from './types';

/**
 * Screen flow: sign in, pick a game, pick a character, play.
 *
 * State currently arrives by polling api.php. That is deliberate for the first
 * playable version -- it exercises exactly the same server path the Firebase
 * listener will, so swapping the transport later changes where snapshots come
 * from and nothing else. Turn-based play tolerates a second of latency easily.
 */

const app = document.getElementById('app')!;

let boot: Bootstrap;
let email: string | null = null;
/** Whether the signed-in address may manage membership. The server checks too. */
let isAdmin = false;
let poller: number | null = null;
let ticker: number | null = null;
let keyHandler: ((event: KeyboardEvent) => void) | null = null;

/**
 * How long a game may go without advancing before every client, rather than
 * just the designated one, will nudge the server. Three polling cycles.
 */
const STALL_MS = 6000;

/**
 * How many seconds must be left before the answer countdown is shown at all.
 *
 * The grace period is a backstop against somebody walking away, not a pace for
 * the table -- so for almost all of it there is nothing worth saying. A clock
 * running from the first second turned every refutation into a race the players
 * could not win, because the answer is forced and there is nothing to decide.
 * Revealed only at the end, it says the one thing it is actually for.
 */
const COUNTDOWN_VISIBLE_AT = 10;

// --------------------------------------------------------------- utilities

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  // Null children are dropped, so a caller can write `cond ? h(...) : null`
  // inline instead of assembling the child list beforehand.
  ...children: (Node | string | null)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'class') el.className = value;
    else if (key.startsWith('data-')) el.setAttribute(key, value);
    else el.setAttribute(key, value);
  }
  el.append(...children.filter((c): c is Node | string => c !== null));
  return el;
}

function show(...nodes: Node[]): void {
  app.replaceChildren(...nodes);
}

/**
 * Tear down whatever the previous screen left running.
 *
 * Every render function calls this first, so it is the one place a screen gets
 * unmounted -- which is why the keyboard listener is dropped here too, rather
 * than surviving into the lobby and arrowing a board that is no longer shown.
 */
function stopPolling(): void {
  if (poller !== null) {
    window.clearInterval(poller);
    poller = null;
  }
  if (ticker !== null) {
    window.clearInterval(ticker);
    ticker = null;
  }
  if (keyHandler !== null) {
    window.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

/**
 * The theme this front door is pinned to.
 *
 * entry.php injects it, so the lobby you see and the games you create both
 * follow from which file you opened -- index.php, lab.php, and one per event
 * after that. Falls back to the configured default so a build opened without a
 * front door still works rather than showing an empty screen.
 */
function pinnedTheme(): string {
  const pinned = (window as { __CLUEME_THEME__?: unknown }).__CLUEME_THEME__;

  return typeof pinned === 'string' && pinned !== '' ? pinned : boot.defaultTheme;
}

/**
 * The display name for a bot's personality, from the server's roster.
 *
 * Falls back to the stored id rather than to a guess: games dealt before a
 * personality existed, or under another edition, still carry whatever id was
 * current then, and showing that is more honest than calling it "normal".
 */
function botLabel(themeId: string | null | undefined, personality: string | null): string {
  const roster = boot.bots?.[themeId || boot.defaultTheme] ?? [];

  return roster.find((b) => b.id === personality)?.label.toLowerCase()
    ?? personality
    ?? 'bot';
}

/** "Scarlett", "Scarlett and Plum", "Scarlett, Plum and Green". */
function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';

  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Ask before deleting, naming whoever else loses the game.
 *
 * Deleting is immediate and there is no undo -- the rows are gone from Firebase
 * -- and any player may do it to a game other people are in the middle of. A
 * bare "are you sure?" is the kind of thing people click through; the names are
 * what makes someone stop. A finished game has nothing left to lose, so it goes
 * with the minimum of ceremony.
 */
function confirmDelete(phase: Phase, others: string[]): boolean {
  if (phase === 'gameover') {
    return window.confirm('Delete this finished game?');
  }

  const rest = others.length === 0
    ? ''
    : ` ${listNames(others)} ${others.length === 1 ? 'is' : 'are'} still playing.`;

  return window.confirm(`Delete this game for everyone?${rest}`);
}

function ago(seconds: number): string {
  const d = Math.max(0, Math.floor(Date.now() / 1000) - seconds);
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

// ------------------------------------------------------------------ sign in

function renderSignIn(prefill = ''): void {
  stopPolling();

  const emailInput = h('input', { type: 'email', placeholder: 'you@example.com', value: prefill });
  const codeInput = h('input', { type: 'text', inputmode: 'numeric', placeholder: '6-digit code', maxlength: '6' });
  const error = h('p', { class: 'error' });
  const note = h('p', { class: 'muted' });

  const sendBtn = h('button', { class: 'primary' }, 'Send me a code');
  const verifyBtn = h('button', { class: 'primary' }, 'Sign in');

  const codeStep = h('div', { class: 'stack hidden' }, codeInput, verifyBtn);

  sendBtn.onclick = async () => {
    error.textContent = '';
    sendBtn.disabled = true;
    try {
      const result = await api.sendCode(emailInput.value.trim(), pinnedTheme());
      note.textContent = result.message;
      codeStep.classList.remove('hidden');
      codeInput.focus();
    } catch (e) {
      error.textContent = describe(e);
    } finally {
      sendBtn.disabled = false;
    }
  };

  verifyBtn.onclick = async () => {
    error.textContent = '';
    verifyBtn.disabled = true;
    try {
      const me = await api.verifyCode(emailInput.value.trim(), codeInput.value.trim(), pinnedTheme());
      email = me.email ?? null;
      isAdmin = me.isAdmin === true;
      await renderLobby();
    } catch (e) {
      error.textContent = describe(e);
      verifyBtn.disabled = false;
    }
  };

  codeInput.onkeydown = (e) => {
    if (e.key === 'Enter') verifyBtn.click();
  };
  emailInput.onkeydown = (e) => {
    if (e.key === 'Enter') sendBtn.click();
  };

  show(
    h(
      'div',
      { class: 'screen centred' },
      h('h1', {}, 'Clue'),
      h('p', { class: 'muted' }, 'Sign in with your email. We will send you a one-time code.'),
      h('div', { class: 'card stack' }, emailInput, sendBtn, codeStep, note, error),
    ),
  );

  emailInput.focus();
}

// -------------------------------------------------------------------- admin

/**
 * Who may play this theme.
 *
 * Shown only to admins, and the server checks that independently -- this screen
 * decides what is offered, never what is permitted. It manages one theme, the
 * one whose front door you came in by, because that is the mental model the
 * rest of the app already uses.
 */
async function renderMembers(): Promise<void> {
  stopPolling();

  const themeId = pinnedTheme();
  const themeName = (boot.themes ?? []).find((t) => t.id === themeId)?.name ?? themeId;

  const list = h('div', { class: 'game-list' });
  const error = h('p', { class: 'error' });
  const note = h('p', { class: 'muted' });
  const input = h('input', { type: 'email', placeholder: 'someone@example.com' });
  const addBtn = h('button', { class: 'primary' }, 'Add');
  const back = h('button', {}, '← Games');

  back.onclick = () => renderLobby();

  const draw = (members: string[]): void => {
    note.textContent = members.length === 1
      ? `1 person can play ${themeName}.`
      : `${members.length} people can play ${themeName}.`;

    list.replaceChildren(
      ...members.map((address) => {
        const remove = h('button', { class: 'delete-link', title: 'Remove from this theme' }, 'Remove');
        remove.onclick = async () => {
          if (!window.confirm(`Remove ${address} from ${themeName}?`)) return;
          remove.disabled = true;
          try {
            draw((await api.memberRemove(themeId, address)).members);
          } catch (e) {
            error.textContent = describe(e);
            remove.disabled = false;
          }
        };

        return h(
          'div',
          { class: 'game-row' },
          h('span', {}, address),
          h('span', { class: 'spacer' }),
          remove,
        );
      }),
    );

    if (members.length === 0) {
      list.append(h('p', { class: 'muted' }, 'Nobody yet. Until somebody is added, only an admin can play this theme.'));
    }
  };

  const add = async (): Promise<void> => {
    error.textContent = '';
    const address = input.value.trim();
    if (address === '') return;

    addBtn.disabled = true;
    try {
      draw((await api.memberAdd(themeId, address)).members);
      input.value = '';
    } catch (e) {
      error.textContent = describe(e);
    } finally {
      addBtn.disabled = false;
      input.focus();
    }
  };

  addBtn.onclick = add;
  input.onkeydown = (e) => {
    if (e.key === 'Enter') add();
  };

  show(
    h(
      'div',
      { class: 'screen stack' },
      h('div', { class: 'row' }, h('h1', {}, `Who can play ${themeName}`), h('span', { class: 'spacer', style: 'margin-left:auto' }), back),
      h('p', { class: 'muted' }, 'Each theme has its own list. Somebody added here can sign in at this theme’s entrance and nowhere else.'),
      h('div', { class: 'row' }, input, addBtn),
      note,
      error,
      list,
    ),
  );

  try {
    draw((await api.members(themeId)).members);
  } catch (e) {
    error.textContent = describe(e);
  }

  input.focus();
}

// -------------------------------------------------------------------- lobby

async function renderLobby(): Promise<void> {
  stopPolling();

  const list = h('div', { class: 'game-list' });
  const error = h('p', { class: 'error' });

  const themeId = pinnedTheme();
  const themeName = (boot.themes ?? []).find((t) => t.id === themeId)?.name ?? themeId;

  const createBtn = h('button', { class: 'primary' }, 'Create a game');
  createBtn.onclick = async () => {
    error.textContent = '';
    createBtn.disabled = true;
    try {
      const created = await api.create(themeId);
      await renderCharacterPicker(created.gameId);
    } catch (e) {
      error.textContent = describe(e);
      createBtn.disabled = false;
    }
  };

  // Admin tools, offered in the lobby only to an admin. The server gates the
  // endpoints themselves, so this is convenience rather than protection.
  const manage = h('button', { title: `Manage who can play ${themeName}` }, 'Members');
  manage.onclick = () => renderMembers();

  const signOut = h('button', {}, 'Sign out');
  signOut.onclick = async () => {
    await api.logout();
    email = null;
    isAdmin = false;
    renderSignIn();
  };

  show(
    h(
      'div',
      { class: 'screen stack' },
      h(
        'div',
        { class: 'row' },
        h('h1', {}, 'Games'),
        h('div', { class: 'spacer', style: 'margin-left:auto' }),
        h('span', { class: 'muted' }, email ?? ''),
        isAdmin ? manage : null,
        signOut,
      ),
      h('div', { class: 'row' }, createBtn, h('span', { class: 'muted' }, themeName)),
      error,
      list,
    ),
  );

  let lastSignature = '';

  const refresh = async () => {
    try {
      const { games } = await api.gamesOf(themeId);

      // Only redraw when the list has genuinely changed. Replacing these rows
      // on every poll would destroy the button a player is in the middle of
      // clicking -- the same way the character picker used to eat clicks.
      // "updatedAt" is deliberately excluded: a game ticking away in the
      // background must not rebuild the row you are aiming at.
      const signature = games
        .map((g) => `${g.gameId}:${g.phase}:${(g.players ?? []).length}`)
        .join('|');

      if (signature === lastSignature) {
        return;
      }
      lastSignature = signature;

      list.replaceChildren(...games.map(gameRow));
      if (games.length === 0) {
        list.append(h('p', { class: 'muted' }, 'No games yet. Create one.'));
      }
    } catch (e) {
      error.textContent = describe(e);
    }
  };

  function gameRow(game: GameSummary): HTMLElement {
    // Defended here as well as on the server: a summary that lost its empty
    // players array in the database must not take the whole lobby down.
    const players = game.players ?? [];

    // Who is at this table, by name. This used to list the characters --
    // "Scarlett, Mustard, Green" -- which said the counters were taken but not
    // whether the people holding them were anybody you were waiting for.
    // People first, then bots, rather than in seat order. The question this
    // line answers is "are the ones I am waiting for here yet", and interleaving
    // makes that a search rather than a glance.
    // A bot is shown as its style and the character it is playing. The robot
    // glyph said "this one is a bot", which the style already says, and spent
    // the space that could have identified which seat it holds.
    const who = listNames([
      ...players.filter((p) => !p.isBot).map((p) => p.label ?? p.name),
      ...players.filter((p) => p.isBot).map((p) => {
        const style = botLabel(game.themeId, p.botDifficulty);

        return p.token ? `${style} ${p.token}` : style;
      }),
    ]) || 'nobody yet';

    const action = h('button', {}, game.phase === 'lobby' ? 'Join' : 'Open');
    action.onclick = () =>
      game.phase === 'lobby' ? renderCharacterPicker(game.gameId) : renderGame(game.gameId);

    // Offered only where the server would allow it: your own games, plus
    // finished ones, which belong to nobody. The server checks again anyway --
    // this decides what to show, not what is permitted.
    const remove = game.mine || game.phase === 'gameover'
      ? h('button', { class: 'delete-link', title: 'Delete this game' }, 'Delete')
      : null;

    if (remove) {
      remove.onclick = async () => {
        if (!confirmDelete(game.phase, game.otherHumans ?? [])) return;

        remove.disabled = true;
        try {
          await api.purge(game.gameId);
          lastSignature = '';
          await refresh();
        } catch (e) {
          error.textContent = describe(e);
          remove.disabled = false;
        }
      };
    }

    return h(
      'div',
      { class: 'game-row' },
      h('div', { class: 'stack' }, h('strong', {}, game.phase === 'lobby' ? 'Waiting' : game.phase), h('span', { class: 'who' }, who)),
      h('span', { class: 'spacer' }),
      h('span', { class: 'muted' }, `${game.seats} seat${game.seats === 1 ? '' : 's'} · ${ago(game.updatedAt)}`),
      remove,
      action,
    );
  }

  await refresh();
  poller = window.setInterval(refresh, 4000);
}

// -------------------------------------------------------- character picker

async function renderCharacterPicker(gameId: string): Promise<void> {
  stopPolling();

  // The game's own theme, not the default one. Picking characters for a lab
  // game from the classic cast would offer six people who are not in it.
  const opening = await api.state(gameId);
  const themeId = opening.public.themeId || boot.defaultTheme;
  const theme = await loadTheme(themeId);

  const grid = h('div', { class: 'character-grid' });
  const error = h('p', { class: 'error' });
  const instruction = h('p', { class: 'muted' });
  const seatNote = h('span', { class: 'muted' });

  let snapshot: GameSnapshot;
  let lastSignature = '';

  // The label keeps its own text in every state. It used to read "Need 3 more"
  // while disabled, which made the one control that cannot be pressed also the
  // only place the requirement was written -- and a disabled button takes no
  // focus, so nobody using a screen reader ever heard it. The shortfall lives
  // in seatNote beside it now, counted in one direction only.
  const startBtn = h('button', { class: 'primary' }, 'Start the game');
  const backBtn = h('button', {}, 'Back to games');
  backBtn.onclick = () => renderLobby();

  // Driven by the server's roster, not a fixed list: the family board offers a
  // Gambler the lab board does not, and a future edition should appear here
  // without touching this file.
  const botOptions = boot.bots?.[themeId]
    ?? [{ id: 'human_like', label: 'Normal', tagline: '', blurb: '' }];

  /**
   * Filling one named seat, opened by tapping an open one.
   *
   * A native <dialog> for the same reasons the suggestion picker is one: focus
   * trapping, Escape and the backdrop are the parts of a modal usually got
   * wrong, and the platform already has them right.
   */
  const seatPicker = h('dialog', { class: 'seat-picker' }) as unknown as HTMLDialogElement;
  const cancelBtn = h('button', {}, 'Cancel');
  cancelBtn.onclick = () => seatPicker.close();

  /**
   * One button per bot this edition offers, each adding on the spot.
   *
   * These act rather than select, and that is what keeps the blurb visible. A
   * dropdown can only show the option already chosen, so "accuses early, can
   * lose" -- the entire reason to prefer a Gambler over a Normal -- used to
   * live in a `title` tooltip, which no phone has ever shown anybody.
   *
   * Passing a suspect fills that seat; passing null fills the next open one.
   *
   * The tiles take the short tagline and the dialog the long blurb. Three
   * descriptions side by side are read by comparison, so what matters there is
   * that they are the same shape; one description on its own is read on its
   * own terms, and can afford to say more.
   */
  function botChoices(suspectId: string | null, detail: 'tagline' | 'blurb'): HTMLElement {
    return h(
      'div',
      { class: 'bot-choices' },
      ...botOptions.map((bot) => {
        const text = bot[detail];
        const choice = h(
          'button',
          { class: 'bot-choice', type: 'button' },
          h('span', { class: 'bot-name' }, bot.label),
          text ? h('span', { class: 'bot-blurb' }, text) : null,
        );
        choice.onclick = () => addBot(bot.id, suspectId);

        return choice;
      }),
    );
  }

  /** The same choices, asked about one seat, so it happens where it applies. */
  function openSeatPicker(suspectId: string, suspectName: string): void {
    seatPicker.replaceChildren(
      h('h2', {}, `Fill ${suspectName}'s seat`),
      botChoices(suspectId, 'blurb'),
      h('div', { class: 'row seat-picker-foot' }, cancelBtn),
    );
    seatPicker.showModal();
  }

  const editionName = boot.themes.find((t) => t.id === themeId)?.name ?? themeId;

  show(
    h(
      'div',
      { class: 'screen stack' },
      h(
        'div',
        { class: 'row title-row' },
        h('h1', {}, 'Set up the table'),
        h('span', { class: 'edition' }, editionName),
      ),
      instruction,
      grid,
      // Beside the grid rather than at the foot of the screen: every error this
      // screen produces is about a seat, and "that character is already taken"
      // two elements below the tile you just aimed at is a message about
      // something you have already stopped looking at.
      error,
      h(
        'div',
        { class: 'fill-zone' },
        h('p', { class: 'zone-label' }, 'Fill a seat with a bot'),
        botChoices(null, 'tagline'),
      ),
      h(
        'div',
        { class: 'row commit-bar' },
        backBtn,
        h('span', { class: 'spacer', style: 'margin-left:auto' }),
        seatNote,
        startBtn,
      ),
      seatPicker as unknown as Node,
    ),
  );

  const refresh = async () => {
    try {
      snapshot = await api.state(gameId);
    } catch (e) {
      error.textContent = describe(e);
      return;
    }

    // Somebody else started it while we were choosing.
    if (snapshot.public.phase !== 'lobby') {
      stopPolling();
      await renderGame(gameId);
      return;
    }

    // Rebuilding the grid on every poll would throw away keyboard focus and
    // could swallow a click that landed mid-replacement. Only redraw when the
    // seats have actually changed.
    const signature = snapshot.public.players.map((p) => p.id).join(',') + `|${snapshot.seat}`;
    if (signature === lastSignature) {
      return;
    }
    lastSignature = signature;

    const seated = new Map(snapshot.public.players.map((p) => [p.id, p]));
    const mine = snapshot.seat !== null;
    const mySuspect = mine ? snapshot.public.players[snapshot.seat!]?.id ?? null : null;

    grid.replaceChildren(
      ...theme.suspects.map((suspect) => {
        const seat = seated.get(suspect.id);
        const isMine = suspect.id === mySuspect;

        // Who has this seat, shown on the tile. Naming the person is the point:
        // "Player" told you a human was there but not which of your friends,
        // and the seat count alone left everyone guessing which character a bot
        // had taken. Seats dealt before labels existed carry none, so the
        // character's own short name stands in rather than an empty badge.
        //
        // An empty seat reads "open", not an offer of a bot. A label on an
        // empty seat states what that seat is *for*, and five of them saying
        // "bot" while you are waiting for friends reads as the screen
        // recommending you give up on them.
        const badge = !seat
          ? 'open'
          : isMine
            ? 'you'
            : seat.isBot
              ? `bot · ${botLabel(snapshot.public.themeId, seat.botDifficulty)}`
              : seat.label ?? seat.shortName ?? seat.name;

        // A button, not a clickable div: these need to be reachable by keyboard
        // and announced by a screen reader, and disabled state has to mean
        // something to assistive technology rather than only to CSS.
        const tile = h(
          'button',
          {
            class: `character${seat ? ' filled' : ' open'}${isMine ? ' mine' : ''}${seat?.isBot ? ' bot' : ''}`,
            type: 'button',
            // The suspect's own colour, so a filled seat is lit in the same hue
            // as its token on the board.
            style: `--seat-color: ${suspect.color}`,
            'aria-label': seat
              ? `${suspect.name} (${badge})`
              : mine
                ? `Put a bot in ${suspect.name}'s seat`
                : `Play as ${suspect.name}`,
          },
          h('span', { class: `tag${seat ? '' : ' vacant'}` }, badge),
          h('span', { class: 'glyph', 'aria-hidden': 'true' }, suspect.token),
          h('span', { class: 'name' }, suspect.name),
        );

        // Only a taken seat is disabled. An open one is not unavailable, it is
        // waiting -- and it used to grey out the moment you sat down, which
        // switched off the half of the grid that adding a bot writes into.
        tile.disabled = seat !== undefined;

        // What an open tile does depends only on whether *you* hold a seat, so
        // it always offers exactly one thing. Unseated -- which is what every
        // arriving human is -- it can only mean "play as this character", so
        // nobody has to read past a bot offer to find their own seat. Seated,
        // joining is impossible for you anyway, which frees it to fill.
        if (!seat) {
          tile.onclick = mine
            ? () => openSeatPicker(suspect.id, suspect.name)
            : async () => {
                try {
                  await api.join(gameId, suspect.id);
                  await refresh();
                } catch (e) {
                  // With several people choosing at once this stops being rare:
                  // the grid is up to one poll out of date, so this seat may
                  // have gone since it was drawn. Redraw at once rather than
                  // leave a stale tile under the pointer for three seconds.
                  error.textContent = describe(e);
                  lastSignature = '';
                  await refresh();
                }
              };
        }

        return tile;
      }),
    );

    const seats = snapshot.public.players.length;
    const need  = boot.minPlayers - seats;

    // Instruction under the heading, status beside the button it gates. These
    // used to be one sentence -- "0 seat(s) filled. Choose a character to
    // join." -- which is why no position for it felt right: an instruction
    // belongs with the thing it instructs and a status with the thing it
    // gates, and glued together it could only ever be in one of those places.
    instruction.textContent = !mine
      ? 'Choose a character to join.'
      : need > 0
        ? 'Wait for the others, or put a bot in an open seat.'
        : 'Everyone is in. Start when you are ready.';

    // One counter, counting one way. This and the button used to disagree in
    // direction -- "1 of 3 seats filled" against "Need 2 more" -- which left
    // the player doing subtraction to check the screen agreed with itself.
    seatNote.textContent = need > 0
      ? `${seats} of ${boot.minPlayers} seats — ${need} more to start`
      // The minimum stops being worth naming once it is met, and saying it
      // anyway produces "4 of 3 seats" at a table of four.
      : `${seats} seats — ready to start`;

    startBtn.disabled = need > 0;
  };

  /**
   * With no seat named, a bot fills from the *back* of the roster. It used to
   * take the first free character, so bots claimed Scarlett, then Mustard --
   * the front of the list, and exactly where an arriving human looks first.
   * Filling backwards leaves the popular end open for people.
   */
  async function addBot(personality: string, suspectId: string | null): Promise<void> {
    error.textContent = '';
    if (seatPicker.open) seatPicker.close();

    const taken  = new Set(snapshot.public.players.map((p) => p.id));
    const target = suspectId ?? [...theme.suspects].reverse().find((s) => !taken.has(s.id))?.id;

    if (target === undefined) {
      error.textContent = 'Every character is taken.';
      return;
    }

    try {
      await api.join(gameId, target, { difficulty: personality });
      await refresh();
    } catch (e) {
      error.textContent = describe(e);
      lastSignature = '';
      await refresh();
    }
  }

  startBtn.onclick = async () => {
    error.textContent = '';
    startBtn.disabled = true;
    try {
      await api.start(gameId);
      stopPolling();
      await renderGame(gameId);
    } catch (e) {
      error.textContent = describe(e);
      startBtn.disabled = false;
    }
  };

  await refresh();
  poller = window.setInterval(refresh, 3000);
}

// --------------------------------------------------------------------- game

async function renderGame(gameId: string): Promise<void> {
  stopPolling();

  let snapshot = await api.state(gameId);
  const theme: Theme = await loadTheme(snapshot.public.themeId || boot.defaultTheme);

  applyPalette(theme);

  const boardEl = h('div', { id: 'board' });
  const seats = h('div', { class: 'seat-list' });
  const hand = h('div', { class: 'hand' });
  const log = h('div', { class: 'log' });
  const controls = h('div', { class: 'row' });
  const error = h('p', { class: 'error' });

  /**
   * Two kinds of error share this one line, and they deserve opposite
   * treatment.
   *
   * Something the player did ("not your turn") is worth reading, so it stays
   * until their next action. A failed background poll is usually a blip -- a
   * dropped packet, a moment of contention on the server -- and the game is
   * still fine, so it is only worth mentioning if it happens twice running,
   * and it clears itself the moment a poll succeeds. Before this, a single
   * hiccup left a permanent red line contradicting a game that was working.
   */
  let pollFailures = 0;
  let errorFromPoll = false;

  /** State for shouldNudge(): when this browser last saw the game move. */
  let lastSeenUpdatedAt: number | undefined;
  let lastChangeAt = Date.now();

  function showError(e: unknown): void {
    error.textContent = describe(e);
    errorFromPoll = false;
  }

  function clearError(): void {
    error.textContent = '';
    errorFromPoll = false;
  }

  function notePollFailure(e: unknown): void {
    if (++pollFailures < 2) return;
    error.textContent = describe(e);
    errorFromPoll = true;
  }

  function notePollSuccess(): void {
    pollFailures = 0;
    if (errorFromPoll) clearError();
  }

  const board = new Board(boardEl, theme, {
    onCellClick: async (point) => {
      if (!isMyMove()) return;
      try {
        await api.act(gameId, 'MOVE', { x: point.x, y: point.y });
        await refresh();
      } catch (e) {
        showError(e);
      }
    },
  });

  // Out of the vertical flow: the centre column is a fixed height budget, and
  // anything stacked below the action bar starts a scrollbar.
  const leave = h('button', { class: 'leave-link', title: 'Back to games' }, '← Games');
  leave.onclick = () => renderLobby();

  // Same treatment as "← Games", in the opposite corner: available without
  // taking any of the height budget the board and controls are fighting over.
  const remove = h('button', { class: 'delete-link corner', title: 'Delete this game for everyone' }, 'Delete game');
  remove.onclick = async () => {
    const pub = snapshot.public;
    const others = pub.players
      .filter((p, i) => !p.isBot && i !== snapshot.seat)
      .map((p) => p.shortName ?? p.name);

    if (!confirmDelete(pub.phase, others)) return;

    remove.disabled = true;
    try {
      await api.purge(gameId);
      stopPolling();
      await renderLobby();
    } catch (e) {
      showError(e);
      remove.disabled = false;
    }
  };

  const notepadEl = h('div', { class: 'notepad' });
  const caseFile = h('div', { class: 'case-file' });

  /**
   * The suggestion and accusation dialogs.
   *
   * A native <dialog> rather than a hand-built overlay: focus trapping, Escape
   * and the backdrop are the parts of a modal usually got wrong, and the
   * platform already has them right.
   *
   * It is the cheaper choice for the layout too. These pickers used to render
   * into the action bar, competing with the centre column's height budget --
   * which is why a suggestion was three bare dropdowns crammed into one row. A
   * dialog costs no layout height, so the controls keep their space and the
   * choice gets room to be read.
   */
  const picker = h('dialog', { class: 'picker' }) as unknown as HTMLDialogElement;

  // Escape and any other native close must leave the state machine in step, or
  // the poller would go on believing a picker is open and stop redrawing.
  picker.addEventListener('close', () => {
    if (openPanel !== null) {
      closePanel();
      draw();
    }
  });

  // The left panel switches between two things that both want the space: My
  // Cards is atmosphere, not function, and the turn history is functional but
  // was buried under the notepad. Splitting them into tabs means an expert
  // player can have the notepad AND the full history above the fold at once,
  // rather than choosing which one to shrink.
  let activeLeftTab: 'cards' | 'history' = (localStorage.getItem('clueme.leftTab') as 'cards' | 'history') || 'cards';

  const cardsTabBtn = h('button', { class: 'tab' }, 'My cards');
  const historyTabBtn = h('button', { class: 'tab' }, 'Turn history');
  const leftTabs = h('div', { class: 'panel-tabs' }, cardsTabBtn, historyTabBtn);

  function setLeftTab(tab: 'cards' | 'history'): void {
    activeLeftTab = tab;
    localStorage.setItem('clueme.leftTab', tab);
    cardsTabBtn.classList.toggle('on', tab === 'cards');
    historyTabBtn.classList.toggle('on', tab === 'history');
    hand.classList.toggle('hidden', tab !== 'cards');
    log.classList.toggle('hidden', tab !== 'history');
  }

  cardsTabBtn.onclick = () => setLeftTab('cards');
  historyTabBtn.onclick = () => setLeftTab('history');
  const clocks = h('div', { class: 'stopwatches' });
  const prompt = h('div', { class: 'prompt-pill' });
  const roller = h('div', { class: 'info-roller' });
  const dpad = h('div', { class: 'dpad' });

  // Accusing lives over the case file, in the Cellar. It is the one action
  // that is about the answer rather than the board, so it sits where the
  // answer does.
  /**
   * A themed label, falling back to the classic wording.
   *
   * Nouns only. The buttons stay verbs -- "Accuse", "Suggest" -- because a
   * theme renaming the *thing* should not turn the control that does it into a
   * noun: the lab calls an accusation a Conclusion, but a button reading
   * "Conclusion" no longer tells you it is something you press.
   */
  const label = (key: string, fallback: string): string => theme.strings?.[key] ?? fallback;

  const accuseBtn = h('button', { class: 'accuse-here danger' }, 'Accuse');

  const leftPanel = h(
    'aside',
    { class: 'side-panel left' },
    leftTabs,
    hand,
    log,
  );
  const rightPanel = h(
    'aside',
    { class: 'side-panel right' },
    h('h3', { class: 'panel-heading' }, label('notepad', 'Elimination note')),
    notepadEl,
  );
  // Tap-outside-to-close target, mobile only -- see .drawer-backdrop in
  // styles.css for why it never has to check what it's covering.
  const drawerBackdrop = h('div', { class: 'drawer-backdrop' });

  // Everything in this column has to fit above the fold, so the order is
  // fixed and each part has a height budget: board, clocks, prompt, roller,
  // actions. Nothing else goes here -- the player list lives in the notepad,
  // where the card counts already are.
  const layout = h(
    'div',
    { class: 'game-layout' },
    leftPanel,
    h(
      'main',
      { class: 'centre-panel' },
      h('div', { class: 'board-wrap' }, boardEl, caseFile, accuseBtn),
      clocks,
      prompt,
      roller,
      h('div', { class: 'action-bar' }, dpad, controls),
      error,
      leave,
      remove,
      picker as unknown as Node,
    ),
    rightPanel,
    drawerBackdrop,
  );

  const notepad = new Notepad(notepadEl, {
    onNotesChanged: (notes) => queueNotes(notes),
  });

  // Drawer pull-tabs, only visible under 900px. Named distinctly from the
  // panel-tabs above (My cards / Turn history) so "tab" does not mean two
  // different things on one screen.
  const leftDrawerPull = h('button', { class: 'pull-tab left', 'aria-label': 'Show cards and history' }, 'Cards');
  const rightDrawerPull = h('button', { class: 'pull-tab right', 'aria-label': 'Show the notepad' }, 'Notepad');

  leftDrawerPull.onclick = () => {
    layout.classList.remove('show-right');
    layout.classList.toggle('show-left');
  };
  rightDrawerPull.onclick = () => {
    layout.classList.remove('show-left');
    layout.classList.toggle('show-right');
  };

  drawerBackdrop.onclick = () => layout.classList.remove('show-left', 'show-right');

  /**
   * Swipe-to-close, one drawer at a time.
   *
   * Only ever removes this panel's own show-* class -- never toggles or adds
   * the other side's -- so a swipe that dismisses one drawer can never pop
   * the other one open behind it. Requires a mostly-horizontal drag before
   * treating a touch as a swipe at all, so scrolling a long card list or the
   * notepad grid isn't misread as a failed close attempt.
   */
  function attachSwipeToClose(panel: HTMLElement, showClass: 'show-left' | 'show-right'): void {
    let startX: number | null = null;
    let startY: number | null = null;
    let horizontal = false;

    panel.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      startX = e.clientX;
      startY = e.clientY;
      horizontal = false;
    });

    panel.addEventListener('pointermove', (e) => {
      if (startX === null || startY === null) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!horizontal && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) horizontal = true;
    });

    const finish = (e: PointerEvent) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      startY = null;
      if (!horizontal) return;
      // Right panel closes by dragging right, off toward its home edge; left
      // panel closes by dragging left, toward its own -- the same direction
      // each drawer travels when the pull-tab closes it.
      const closingSwipe = showClass === 'show-right' ? dx > 40 : dx < -40;
      if (closingSwipe) layout.classList.remove(showClass);
    };

    panel.addEventListener('pointerup', finish);
    panel.addEventListener('pointercancel', finish);
  }

  attachSwipeToClose(leftPanel, 'show-left');
  attachSwipeToClose(rightPanel, 'show-right');

  layout.append(leftDrawerPull, rightDrawerPull);
  hand.className = 'card-shelf';
  log.className = 'history';
  setLeftTab(activeLeftTab);

  show(layout);

  /**
   * Marks are saved in the background, batched.
   *
   * Cycling a cell is a rapid, repeated action -- a round trip per click would
   * make the grid feel stuck. The local copy wins until the save lands, which
   * also stops a poll arriving mid-save from reverting a mark.
   */
  let pendingNotes: Record<string, NoteSymbol> | null = null;
  let noteTimer: number | null = null;

  function queueNotes(notes: Record<string, NoteSymbol>): void {
    pendingNotes = notes;

    if (noteTimer !== null) window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(async () => {
      const sending = pendingNotes;
      try {
        await api.saveNotes(gameId, sending ?? {});
        // Only release the local copy if nothing changed while saving.
        if (pendingNotes === sending) pendingNotes = null;
      } catch (e) {
        showError(e);
      }
    }, 600);
  }

  const isMyMove = () =>
    snapshot.seat !== null &&
    snapshot.public.turnIndex === snapshot.seat &&
    snapshot.public.phase === 'moving' &&
    snapshot.public.currentSuggestion === null &&
    snapshot.public.currentSteps > 0;

  /**
   * Which transient panel the player has open, if any.
   *
   * The poller redraws every couple of seconds, and an unconditional redraw
   * tears down whatever the player is in the middle of -- open the suggestion
   * picker and it vanishes before you can choose. While a panel is open the
   * controls are left alone; the board and side panel still update around it.
   */
  let openPanel: 'suggest' | 'accuse' | null = null;

  /** A panel stops making sense if the game moves on without you. */
  function panelStillValid(): boolean {
    const pub = snapshot.public;
    const seat = snapshot.seat;
    if (seat === null || pub.phase === 'gameover') return false;

    if (openPanel === 'accuse') {
      return !pub.players[seat]?.hasLost;
    }
    return pub.phase === 'moving' && pub.turnIndex === seat && pub.currentSuggestion === null;
  }

  function closePanel(): void {
    openPanel = null;
    if (picker.open) picker.close();
  }

  /**
   * The suggestion and accusation dialogs.
   *
   * A native <dialog> rather than a hand-built overlay: focus trapping, Escape,
   * and the backdrop are the parts of a modal that are usually got wrong, and
   * the platform already has them right.
   *
   * It is also the cheaper choice for the layout. These pickers used to render
   * into the action bar, competing with the centre column's height budget --
   * which is why a suggestion was three bare dropdowns crammed into one row. A
   * dialog costs no layout height at all, so the controls below the board keep
   * their space and the choice gets room to be readable.
   */
  /** A labelled row, so the dialog reads as a form and not three loose menus. */
  function field(text: string, select: HTMLSelectElement, fixed = false): HTMLElement {
    select.disabled = fixed;

    return h(
      'label',
      { class: `field${fixed ? ' fixed' : ''}` },
      h('span', {}, text),
      select as unknown as Node,
    );
  }

  function showPicker(opts: {
    kind: 'suggest' | 'accuse';
    title: string;
    warning?: string;
    fields: HTMLElement[];
    confirm: string;
    confirmClass: string;
    onConfirm: () => void;
  }): void {
    openPanel = opts.kind;

    const go = h('button', { class: opts.confirmClass, type: 'button' }, opts.confirm);
    const cancel = h('button', { type: 'button' }, 'Cancel');
    go.onclick = opts.onConfirm;
    cancel.onclick = () => picker.close();

    // replaceChildren is the native one and will not take a null the way h()
    // does, so the optional warning is filtered rather than passed through.
    picker.replaceChildren(
      ...[
        h('h3', {}, opts.title),
        opts.warning ? h('p', { class: 'warn' }, opts.warning) : null,
        ...opts.fields,
        h('div', { class: 'row actions' }, go, cancel),
      ].filter((n): n is HTMLElement => n !== null),
    );

    picker.showModal();

    // Enter confirms, which is what a three-menu form invites you to press.
    picker.onkeydown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        go.click();
      }
    };

    (picker.querySelector('select:not(:disabled)') as HTMLSelectElement | null)?.focus();
  }

  async function act(type: string, payload: Record<string, unknown> = {}): Promise<void> {
    clearError();
    closePanel();
    try {
      await api.act(gameId, type, payload);
      await refresh();
    } catch (e) {
      showError(e);
      draw();
    }
  }

  function draw(): void {
    const pub = snapshot.public;
    const seat = snapshot.seat;

    drawPrompt();

    // Two lines of what just happened: the latest, and the one before it
    // dimmed. Any more and it competes with the board for attention.
    roller.replaceChildren(
      h('div', { class: 'now' }, pub.lastAction || '…'),
      h('div', { class: 'before' }, pub.prevAction || ''),
    );

    board.render(pub, seat);

    if (isMyMove() && seat !== null) {
      const me = pub.players[seat]!;
      board.setReachable(board.computeReachable({ x: me.x, y: me.y }, pub.currentSteps));
    } else {
      board.clearReachable();
    }

    seats.replaceChildren(
      ...pub.players.map((p, i) => {
        const suspect = theme.suspects.find((s) => s.id === p.id);
        const classes = ['seat'];
        if (i === pub.turnIndex && pub.phase !== 'gameover') classes.push('turn');
        if (p.hasLost) classes.push('out');
        return h(
          'div',
          { class: classes.join(' ') },
          h('span', { class: 'glyph' }, suspect?.token ?? '?'),
          h('span', {}, p.shortName ?? p.name),
          h('span', { class: 'tag' }, `${pub.playerCardCounts[i] ?? 0} cards${p.isBot ? ' · bot' : ''}${i === seat ? ' · you' : ''}`),
        );
      }),
    );

    // Cards are shown large and illustrated: they carry the atmosphere rather
    // than doing a job, which is why they get a panel to themselves.
    hand.replaceChildren(
      ...snapshot.hand.map((card) => {
        const tile = h('div', { class: 'big-card', 'data-type': cardType(card) },
          h('span', { class: 'caption' }, nameOf(card)));
        const art = artFor(card);
        if (art) tile.style.backgroundImage = `url("${art}")`;
        return tile;
      }),
    );

    drawCaseFile();
    drawStopwatches();
    drawDpad();

    // Do not clobber marks the player has made that are still on their way to
    // the server -- the local copy is newer than whatever the poll returned.
    notepad.update(
      snapshot.notepad ?? null,
      pendingNotes ?? snapshot.notes ?? {},
      pub.players,
      pub.playerCardCounts ?? [],
      snapshot.seat,
    );

    drawHistory();

    drawControls();

    // A spectator has no claim on a game still being played, so do not offer
    // them something the server would refuse. Once it is over, anyone may tidy
    // it away.
    remove.hidden = snapshot.seat === null && pub.phase !== 'gameover';
  }

  function drawControls(): void {
    const pub = snapshot.public;
    const seat = snapshot.seat;

    // Leave an open picker alone, so a poll cannot close it mid-choice. If the
    // game has moved past the point where it makes sense, drop it and carry on.
    if (openPanel !== null) {
      if (panelStillValid()) {
        return;
      }
      closePanel();
    }

    const buttons: HTMLElement[] = [];

    if (seat === null) {
      controls.replaceChildren(h('span', { class: 'muted' }, 'You are watching this game.'));
      return;
    }

    if (pub.phase === 'gameover') {
      const winner = pub.winner === null ? 'Nobody' : pub.players[pub.winner]?.name;
      const answer = pub.caseFile
        ? ` It was ${nameOf(pub.caseFile.suspect)} with the ${nameOf(pub.caseFile.weapon)} in the ${nameOf(pub.caseFile.room)}.`
        : '';
      accuseBtn.hidden = true;
      controls.replaceChildren(h('strong', {}, `${winner} wins.${answer}`));
      return;
    }

    const mine = pub.turnIndex === seat;
    const me = pub.players[seat]!;

    if (pub.phase === 'rolling' && mine) {
      const roll = h('button', { class: 'primary' }, pub.rollsCount === 0 ? 'Roll' : 'Roll again');
      roll.onclick = () => act('ROLL');
      buttons.push(roll);
    }

    if (pub.phase === 'disproving' && pub.disproverIndex === seat) {
      const left = answerCountdown();
      const asked = pub.currentSuggestion;

      // The button says what pressing it will say. The answer is derived from
      // the hand and cannot be chosen -- doAnswer() rejects any claim that
      // disagrees -- so "Answer the suggestion" was asking a player to confirm
      // something nobody had told them. Reading your own hand back to yourself
      // concedes nothing: the deduction worth having is what everyone ELSE
      // answers, and that is what the pause is now long enough to do.
      const holds = asked !== null
        && [asked.suspect, asked.weapon, asked.room].some((card) => (snapshot.hand ?? []).includes(card));
      const tail = left !== null && left <= COUNTDOWN_VISIBLE_AT ? ` (${left})` : '';

      const answer = h(
        'button',
        { class: 'primary' },
        `${holds ? 'Yes — I hold one of them' : 'No — I hold none of them'}${tail}`,
      );
      answer.onclick = () => act('DISPROVE');
      buttons.push(answer);
    }

    if (pub.phase === 'moving' && mine && pub.currentSuggestion === null) {
      const room = board.roomAt({ x: me.x, y: me.y });
      const roomDef = theme.rooms.find((r) => r.id === room);

      if (roomDef?.isCard) {
        const suggest = h('button', { class: 'primary' }, `Suggest in the ${roomDef.name}`);
        suggest.onclick = () => openSuggest(roomDef);
        buttons.push(suggest);
      }
      if (roomDef?.passageTo) {
        const target = theme.rooms.find((r) => r.id === roomDef.passageTo);
        const warp = h('button', {}, `✨ Passage to the ${target?.name ?? '?'}`);
        warp.onclick = () => act('WARP');
        buttons.push(warp);
      }
    }

    if (pub.phase === 'moving' && mine) {
      const end = h('button', {}, 'End turn');
      end.onclick = () => act('END_TURN');
      buttons.push(end);
    }

    // Accusing has its own home over the case file, so it is not in this list.
    // (Game-over already returned above, which is why that is not checked here.)
    accuseBtn.hidden = me.hasLost;
    accuseBtn.onclick = () => openAccuse();

    controls.replaceChildren(...buttons);
  }

  function pick(label: string, options: { id: string; name: string }[]): HTMLSelectElement {
    const select = document.createElement('select');
    select.title = label;
    for (const option of options) {
      select.append(new Option(option.name, option.id));
    }
    return select;
  }

  function openSuggest(room: { id: string; name: string }): void {
    const suspect = pick(label('suspect', 'Suspect'), theme.suspects);
    const weapon = pick(label('weapon', 'Weapon'), theme.weapons);

    // The room is shown as a third menu, filled in and disabled. It is not a
    // choice: a suggestion is definitionally "the room I am standing in", and
    // the server derives it from the player's position rather than trusting
    // anything the client sends. Showing it greyed says that better than the
    // line of prose this used to carry, and keeps the three cards of a
    // suggestion visible together.
    const here = pick(label('room', 'Room'), [room]);

    showPicker({
      kind: 'suggest',
      title: label('suggestion', 'Suggestion'),
      fields: [
        field(label('suspect', 'Suspect'), suspect),
        field(label('weapon', 'Weapon'), weapon),
        field(label('room', 'Room'), here, true),
      ],
      confirm: 'Suggest',
      confirmClass: 'primary',
      onConfirm: () => {
        picker.close();
        act('SUGGEST', {
          suspect: `suspect:${suspect.value}`,
          weapon: `weapon:${weapon.value}`,
        });
      },
    });
  }

  function openAccuse(): void {
    openPanel = 'accuse';

    const suspect = pick(label('suspect', 'Suspect'), theme.suspects);
    const weapon = pick(label('weapon', 'Weapon'), theme.weapons);
    const room = pick(label('room', 'Room'), theme.rooms.filter((r) => r.isCard));

    showPicker({
      kind: 'accuse',
      title: label('accusation', 'Accusation'),
      // The consequence belongs here, in a sentence, rather than crammed onto
      // the button it used to be part of. All three cards are yours to choose:
      // unlike a suggestion, an accusation names a room you need not be in.
      warning: 'If you are wrong, you are out of the game — you keep answering, but you cannot win.',
      fields: [
        field(label('suspect', 'Suspect'), suspect),
        field(label('weapon', 'Weapon'), weapon),
        field(label('room', 'Room'), room),
      ],
      confirm: 'Accuse',
      confirmClass: 'danger',
      onConfirm: () => {
        picker.close();
        act('ACCUSE', {
          suspect: `suspect:${suspect.value}`,
          weapon: `weapon:${weapon.value}`,
          room: `room:${room.value}`,
        });
      },
    });
  }

  /** Full URL for a card's artwork, resolved against the theme folder. */
  function artFor(card: string): string | null {
    const ref = cardRef(card);
    const image =
      cardType(card) === 'suspect'
        ? theme.suspects.find((s) => s.id === ref)?.cardImage
        : cardType(card) === 'weapon'
          ? theme.weapons.find((w) => w.id === ref)?.cardImage
          : theme.rooms.find((r) => r.id === ref)?.cardImage;

    return image ? `themes/${theme.id}/${image}` : null;
  }

  /**
   * Three cards face down over the centrepiece, revealed when the game ends.
   */
  function drawCaseFile(): void {
    const answer = snapshot.public.caseFile;

    if (caseFile.childElementCount === 0) {
      for (let i = 0; i < 3; i++) {
        const card = h('div', { class: 'case-card' });
        card.style.backgroundImage = `url("themes/${theme.id}/assets/card-back.png")`;
        caseFile.appendChild(card);
      }
    }

    if (!answer) {
      caseFile.classList.remove('revealed');
      return;
    }

    [answer.suspect, answer.weapon, answer.room].forEach((card, i) => {
      const face = caseFile.children[i] as HTMLElement | undefined;
      const art = artFor(card);
      if (face && art) face.style.backgroundImage = `url("${art}")`;
    });
    caseFile.classList.add('revealed');
  }

  /**
   * Cumulative thinking time per player.
   *
   * This is a social instrument, not a diagnostic -- whoever has kept everyone
   * waiting longest is buying the drinks. So the leader is marked plainly
   * rather than left for people to work out, and the running clock ticks
   * between polls so the pressure is visible in real time.
   */
  /**
   * One line per player, however many there are.
   *
   * Six players have to fit without wrapping, so the "who is buying" signal is
   * a single icon beside the time rather than a second line of text.
   */
  function drawStopwatches(): void {
    const pub = snapshot.public;
    const totals = elapsedTotals();
    const slowest = Math.max(0, ...totals);
    const worst = totals.indexOf(slowest);

    clocks.replaceChildren(
      ...pub.players.map((p, i) => {
        const suspect = theme.suspects.find((s) => s.id === p.id);
        const running = i === pub.swActivePlayer && pub.phase !== 'gameover';

        const clock = h('span', { class: 'clock' }, mmss(totals[i] ?? 0));
        if (i === worst && slowest > 0) {
          clock.append(h('span', { class: 'beer', title: 'Buying the drinks' }, '🍺'));
        }

        return h(
          'div',
          { class: `stopwatch${running ? ' active' : ''}${i === snapshot.seat ? ' mine' : ''}` },
          h('span', { class: 'who' }, `${suspect?.token ?? ''} ${p.shortName ?? p.name}`),
          clock,
        );
      }),
    );
  }

  /**
   * The one line that says what is expected of you right now.
   */
  function drawPrompt(): void {
    const pub = snapshot.public;
    const seat = snapshot.seat;
    const mine = seat !== null && pub.turnIndex === seat;

    let text: string;
    let tone = '';

    if (pub.phase === 'gameover') {
      const winner = pub.winner === null ? 'Nobody' : pub.players[pub.winner]?.name;
      text = `${winner} wins!`;
      tone = ' win';
    } else if (pub.phase === 'disproving') {
      const who = pub.players[pub.disproverIndex];

      // The count is still shown to the whole table, but only near the end.
      // Shown from the first second it was read as "hurry up", and since the
      // answer is forced there was nothing to hurry -- so the urgency went at
      // whoever was answering. Held back, it means what it should: we think
      // this person has gone.
      const left = answerCountdown();
      const closing = left !== null && left <= COUNTDOWN_VISIBLE_AT;
      const tail = closing ? ` (${left})` : '';

      const isBot = who?.isBot === true;

      text = pub.disproverIndex === seat
        ? `Your answer!${tail}`
        : `Waiting on ${seatName(who)}${tail}`;

      tone = pub.disproverIndex === seat
        ? ' mine'
        // Only a human is worth prodding, so only a human gets the pulse.
        : isBot ? '' : closing ? ' waiting closing' : ' waiting';
    } else if (!mine) {
      const who = pub.players[pub.turnIndex];
      text = pub.currentSuggestion !== null
        ? `${seatName(who)} suggesting`
        : `${seatName(who)}'s turn`;
    } else if (pub.phase === 'rolling') {
      text = pub.rollsCount === 0 ? 'Your turn!' : `Your turn! (Roll 1: ${pub.currentSteps})`;
      tone = ' mine';
    } else {
      text = pub.currentSteps > 0 ? `Your turn! (${pub.currentSteps} steps left)` : 'Your turn!';
      tone = ' mine';
    }

    prompt.className = `prompt-pill${tone}`;
    prompt.textContent = text;
  }

  /**
   * Arrow keys for one-square moves, alongside clicking a highlighted square.
   * Fiddly on a laptop trackpad otherwise, and it is what the family is used to.
   */
  function drawDpad(): void {
    const pub = snapshot.public;
    const seat = snapshot.seat;
    const active = isMyMove() && seat !== null;
    const me = seat !== null ? pub.players[seat] : undefined;

    const arrows: [string, number, number, string, string][] = [
      ['▲', 0, -1, 'Move up', 'ArrowUp'],
      ['◀', -1, 0, 'Move left', 'ArrowLeft'],
      ['▼', 0, 1, 'Move down', 'ArrowDown'],
      ['▶', 1, 0, 'Move right', 'ArrowRight'],
    ];

    dpad.replaceChildren(
      ...arrows.map(([glyph, dx, dy, label, key]) => {
        const button = h(
          'button',
          { class: 'arrow', 'aria-label': label, title: `${label} (${key.replace('Arrow', '')} arrow)`, 'data-key': key },
          glyph,
        );
        const to = me ? { x: me.x + dx, y: me.y + dy } : null;

        // Grey out directions that are not a legal step, so the pad shows
        // where you can actually go.
        button.disabled = !active || !to || board.computeReachable({ x: me!.x, y: me!.y }, 1)
          .every((p) => p.x !== to.x || p.y !== to.y);

        button.onclick = () => to && act('MOVE', { x: to.x, y: to.y });
        return button;
      }),
    );
  }

  /**
   * Whether this browser should nudge the server to get on with it.
   *
   * The server only plays bot turns, and only answers for an absent player,
   * when something asks it to -- and the asking is done by whoever is polling.
   * The obvious implementation is "everyone asks", which is what this used to
   * do, and it scales badly in exactly the wrong direction: every human in the
   * game fires a tick at the same moment, one wins the game's lock and the rest
   * get a 409 for their trouble. Four players meant four ticks per cycle, three
   * of them pure waste, and each of those wasted ones still cost four Firebase
   * round trips just to discover the lock was taken.
   *
   * So one browser does it. Every client holds the same player list and so
   * reaches the same answer without exchanging a word -- no election, no extra
   * request, no shared state.
   */
  function shouldNudge(): boolean {
    const pub = snapshot.public;
    if (pub.phase === 'gameover') return false;

    const waitingOn = pub.phase === 'disproving' ? pub.disproverIndex : pub.turnIndex;
    const owed = pub.players[waitingOn]?.isBot === true || answerCountdown() === 0;
    if (!owed) return false;

    if (snapshot.seat === pub.players.findIndex((p) => !p.isBot)) return true;

    // Unless that player has shut their laptop, in which case the game would
    // sit there forever waiting for a nudge nobody is sending. A tick that does
    // anything moves updatedAt, so a game that has not advanced in three cycles
    // has nobody driving it, and everyone else stops deferring. Two clients
    // arriving here together is harmless: one takes the lock, the other now
    // gets its 409 and quietly gives up rather than retrying.
    return Date.now() - lastChangeAt > STALL_MS;
  }

  /** Accumulated seconds per seat, including the clock currently running. */
  function elapsedTotals(): number[] {
    const pub = snapshot.public;
    const now = Math.floor(Date.now() / 1000);
    const live = pub.phase !== 'gameover' ? pub.swActivePlayer : -1;

    return pub.players.map((_, i) => {
      const base = pub.swTimes?.[i] ?? 0;
      return i === live ? base + Math.max(0, now - (pub.swActiveAt ?? now)) : base;
    });
  }

  /**
   * Seconds left before the server answers the open suggestion itself, or null
   * when nothing is counting down.
   *
   * Only humans are counted: a bot answers the instant it is asked, so a table
   * of bots never pauses and showing them a countdown would be a lie. Clamped
   * at zero rather than going negative, because the server applies the answer
   * on its next tick, which can be a second or two after the count runs out.
   */
  function answerCountdown(): number | null {
    const pub = snapshot.public;

    if (pub.phase !== 'disproving') return null;

    const who = pub.players[pub.disproverIndex];
    if (!who || who.isBot) return null;

    // The stopwatch is already on whoever is being waited for, so it doubles as
    // "pending since" -- the same field the server gates on.
    if (pub.swActivePlayer !== pub.disproverIndex) return null;

    const now = Math.floor(Date.now() / 1000);
    const waited = Math.max(0, now - (pub.swActiveAt ?? now));

    return Math.max(0, boot.answerGrace - waited);
  }

  /**
   * What to call a seat in the prompt.
   *
   * A person gets their own name. The character's name says which counter is on
   * the board, which is not the same question as who the table is waiting for --
   * "Peacock answering" is a poor thing to say to a room of people who know
   * each other by name, and the whole point of naming is that a pause belongs
   * to somebody rather than to a clock.
   *
   * A bot keeps its character name, because for a bot that IS its name.
   */
  function seatName(
    who: { isBot: boolean; label: string | null; shortName: string; name: string } | undefined,
  ): string {
    if (who === undefined) {
      return 'someone';
    }

    return who.isBot ? who.shortName ?? who.name : who.label ?? who.shortName ?? who.name;
  }

  function mmss(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  /**
   * Turn history, one block per suggestion.
   *
   * The 2025 log put the suggestion and each answer on separate lines, so
   * reading back through a game meant reassembling every exchange by eye. One
   * block per question, with the answers inline, is the whole fix.
   */
  function drawHistory(): void {
    const turns: HTMLElement[] = [];
    let current: { el: HTMLElement; answers: HTMLElement } | null = null;
    let round = 0;

    for (const event of snapshot.events) {
      if (event.type === 'suggestion') {
        round++;
        const answers = h('div', { class: 'answers' });
        const block = h(
          'div',
          { class: 'turn-entry' },
          h(
            'div',
            { class: 'headline' },
            h('span', { class: 'round' }, String(round)),
            h('span', { class: 'asker' }, shortName(event.suggester)),
            h('span', { class: 'claim' },
              [event.suspect, event.weapon, event.room].map((c) => nameOf(String(c))).join(' · ')),
          ),
          answers,
        );
        current = { el: block, answers };
        turns.push(block);
        continue;
      }

      if (event.type === 'response' && current) {
        // Their words, not mine: the family has been saying these for months.
        const said = event.hasCard ? 'No! (Has Card)' : 'Dunno';
        current.answers.append(
          h('span', { class: `answer${event.hasCard ? ' has-card' : ''}` },
            `${shortName(event.responder)} ${said}`),
        );
        continue;
      }

      if (event.type === 'accusation') {
        turns.push(
          h(
            'div',
            { class: `turn-entry accusation${event.correct ? ' correct' : ''}` },
            h(
              'div',
              { class: 'headline' },
              h('span', { class: 'asker' }, shortName(event.accuser)),
              h('span', { class: 'claim' },
                `accuses ${[event.suspect, event.weapon, event.room].map((c) => nameOf(String(c))).join(' · ')}`),
            ),
            h('div', { class: 'answers' },
              h('span', { class: `answer${event.correct ? '' : ' has-card'}` },
                event.correct ? 'Correct — game over' : 'Wrong — out of the game')),
          ),
        );
        current = null;
      }
    }

    log.replaceChildren(...turns.reverse());
  }

  function shortName(seat: unknown): string {
    const p = snapshot.public.players[Number(seat)];
    return p?.shortName ?? p?.name ?? '?';
  }

  function nameOf(card: string): string {
    const ref = cardRef(card);
    switch (cardType(card)) {
      case 'suspect': return theme.suspects.find((s) => s.id === ref)?.name ?? ref;
      case 'weapon': return theme.weapons.find((w) => w.id === ref)?.name ?? ref;
      default: return theme.rooms.find((r) => r.id === ref)?.name ?? ref;
    }
  }

  async function refresh(): Promise<void> {
    try {
      snapshot = await api.state(gameId);

      // Track how long *this* browser has gone without seeing the game move,
      // for the stall check in shouldNudge(). Deliberately local elapsed time
      // rather than comparing a server timestamp against Date.now(): four
      // laptops round a table do not agree on the clock, but each can count
      // its own seconds.
      if (snapshot.public.updatedAt !== lastSeenUpdatedAt) {
        lastSeenUpdatedAt = snapshot.public.updatedAt;
        lastChangeAt = Date.now();
      }

      draw();
      notePollSuccess();

      if (shouldNudge()) {
        await api.tick(gameId).catch(() => undefined);
      }
    } catch (e) {
      notePollFailure(e);
    }
  }

  draw();
  poller = window.setInterval(refresh, 2000);

  // The clocks tick between polls. Without this the running total would jump
  // in two-second steps, which rather undercuts watching someone squirm.
  /**
   * Arrow keys move, by pressing the d-pad button for that direction.
   *
   * Going through the button rather than calling act() directly means the keys
   * inherit its disabled state for free, so a key can never make a move the pad
   * would have refused -- there is one set of rules about what is a legal step,
   * not two that can drift.
   */
  keyHandler = (event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    // Never steal a keystroke aimed at a field, or at a picker the player has
    // open -- arrows moving the token while somebody chooses cards would be a
    // surprise.
    const target = event.target;
    if (
      openPanel !== null ||
      (target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)))
    ) {
      return;
    }

    const button = dpad.querySelector(`[data-key="${event.key}"]`);
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    button.click();
  };
  window.addEventListener('keydown', keyHandler);

  ticker = window.setInterval(() => {
    if (snapshot.public.phase === 'gameover') return;

    drawStopwatches();

    // The answer countdown lives in the prompt and on the button, so both have
    // to keep up with it between polls -- a number that only moves every two
    // seconds reads as broken rather than as a countdown.
    if (answerCountdown() !== null) {
      drawPrompt();
      drawControls();
    }
  }, 1000);
}

function applyPalette(theme: Theme): void {
  const map: Record<string, string> = {
    bg: '--bg',
    wall: '--wall',
    hall: '--hall',
    room: '--room',
    door: '--door',
    panelBg: '--panel-bg',
  };
  for (const [key, variable] of Object.entries(map)) {
    const value = theme.palette?.[key];
    if (value) document.documentElement.style.setProperty(variable, value);
  }
}

// ---------------------------------------------------------------------- boot

async function start(): Promise<void> {
  try {
    boot = await api.bootstrap();
    const me = await api.me();

    if (me.signedIn) {
      email = me.email ?? null;
      isAdmin = me.isAdmin === true;
      await renderLobby();
    } else {
      renderSignIn();
    }
  } catch (e) {
    show(
      h(
        'div',
        { class: 'screen centred' },
        h('h1', {}, 'Cannot reach the server'),
        h('p', { class: 'muted' }, describe(e)),
      ),
    );
  }
}

void start();
